import React from 'react';
import { NextUI } from '@/components/NextUI';
import Button from '@/components/NextUI/Button';
import { useAppDispatch } from '@/hooks/redux';
import { showToast } from '@/store/slices/toastActions';
import useAuth from '@/hooks/useAuth';
import { z } from 'zod';
import { emit } from '@/utils/emitter';

const profileSchema = z.object({
  name: z
    .string()
    .min(1, { message: 'Name cannot be empty' })
    .max(100, { message: 'Name must be at most 100 characters' }),
});

const ProfileModal = ({
  hideModal,
  data,
}: {
  hideModal: (data?: any) => void;
  data: { pendingName?: string };
}) => {
  const dispatch = useAppDispatch();
  const { user, fbUser, updateUser, error, loginWithCustomToken } = useAuth();
  const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = React.useState(false);

  const handleUpdateProfile = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    const formData = new FormData(e.currentTarget);
    const payload = {
      name: formData.get('name') as string,
    };

    const validationResult = profileSchema.safeParse(payload);

    if (!validationResult.success) {
      const formErrors: { [key: string]: string } = {};
      validationResult.error.errors.forEach((error) => {
        formErrors[error.path[0]] = error.message;
      });
      setErrors(formErrors);
      setIsLoading(false);
      return;
    }

    try {
      if (payload.name === user?.name) {
        hideModal();
        return;
      }

      const updatePayload = {
        uid: user?.uid,
        name: payload.name,
        email: user?.email as string,
      };

      hideModal();
      await updateUser(updatePayload);
      await loginWithCustomToken();
    } catch (err) {
      dispatch(
        showToast({
          text: error ?? 'Something went wrong',
          variant: 'danger',
        })
      );
      console.error('Error updating profile:', err);
    } finally {
      emit('UPDATED_USER_NAME');
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    emit('UPDATED_USER_NAME');
    hideModal();
  };

  return (
    <NextUI.Modal
      className="max-w-[500px]"
      classNames={{ content: 'py-8 !px-4 md:!px-8' }}
      defaultOpen={true}
      name={'ProfileModal'}
      onClose={handleCancel}
    >
      <h2 className="h5 font-semibold text-start text-dark">
        Change Your Info
      </h2>

      <form onSubmit={handleUpdateProfile} className="mt-4">
        <div className="space-y-5">
          <NextUI.Input
            type="text"
            name="name"
            formNoValidate
            variant="outline"
            radius="sm"
            placeholder="Enter your name"
            defaultValue={data?.pendingName || user?.name}
            isInvalid={!!errors.name}
            errorMessage={errors.name}
            onChange={(e) => emit('UPDATING_USER_NAME', e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-4 mt-8">
          <Button
            variant="outline"
            className="w-[160px]"
            onClick={handleCancel}
            type="button"
          >
            Cancel
          </Button>
          <Button className="w-[160px]" type="submit" isLoading={isLoading}>
            Change
          </Button>
        </div>
      </form>
    </NextUI.Modal>
  );
};

export default ProfileModal;
